import { Assets, Rectangle, Texture } from "pixi.js";
import type { AssetManifest, AssetManifestAsset, AssetTrim } from "./AssetManifest";
import { BOOT_REQUEST_TIMEOUT_MS } from "../boot/retry/BootRequestTimeout";
import type { JsonAssetSource } from "./JsonAssetSource";
import type { BundleRef } from "../audio/banks/types";

/**
 * The app's bundles over Pixi `Assets`, as the built `manifest.json` lists them:
 *
 * - bundles are loaded by NAME (`SHELL_BUNDLE_NAMES`), with progress
 * - `getLoadedAssetOrThrow(bundle, path)` returns an already-loaded asset by its
 *   bundle-relative path (`symbols/atlases/symbols`) — never loads lazily; a standalone image
 *   with transparent margins comes out framed to its opaque region (`data.trim`)
 * - `resolveUrl(bundle, path)` for things that bypass Pixi (audio, HTML, the images the DOM paints);
 *   a sprite sheet's path gives its image, and a JSON or `.atlas` file is reached by alias (`AssetManifest`)
 * - `bundleJson(bundle)` is a `JsonAssetSource` for the locale loaders
 *
 * Remote / operator bundles register later via `addBundle` (branding).
 */
export class AssetRegistry {
  private readonly files: Record<string, string>;
  private readonly loaded = new Set<string>();
  private readonly known = new Set<string>();
  /** Alias → the opaque region recorded for that image (`data.trim`: the build's, or a remote bundle descriptor's). */
  private readonly trims = new Map<string, AssetTrim>();
  /** Alias → the framed texture handed out for it, made once so every caller gets the same object. */
  private readonly framed = new Map<string, Texture>();

  private constructor(
    manifest: AssetManifest,
    /** Absolute or root-relative URL prefix the manifest paths are served under, no trailing slash. */
    private readonly baseUrl: string,
  ) {
    this.files = manifest.files;
    for (const bundle of manifest.bundles) {
      this.known.add(bundle.name);
      for (const asset of bundle.assets) {
        if (asset.data?.trim) this.trims.set(asset.alias, asset.data.trim);
      }
    }
  }

  /** Fetches the manifest and initialises Pixi Assets with it. Call once per app. */
  public static async load(assetsBaseUrl: string): Promise<AssetRegistry> {
    const baseUrl = assetsBaseUrl.replace(/\/$/, "");
    const response = await fetch(`${baseUrl}/manifest.json`, { signal: AbortSignal.timeout(BOOT_REQUEST_TIMEOUT_MS) });
    if (!response.ok) throw new Error(`AssetRegistry: manifest fetch failed status=${response.status} url=${baseUrl}/manifest.json`);
    const manifest = (await response.json()) as AssetManifest;
    await Assets.init({ manifest: { bundles: manifest.bundles }, basePath: baseUrl });
    return new AssetRegistry(manifest, baseUrl);
  }

  public hasBundle(name: string): boolean {
    return this.known.has(name);
  }

  public isBundleLoaded(name: string): boolean {
    return this.loaded.has(name);
  }

  public async loadBundle(name: string, onProgress?: (progress01: number) => void): Promise<void> {
    if (this.loaded.has(name)) {
      onProgress?.(1);
      return;
    }
    if (!this.known.has(name)) throw new Error(`AssetRegistry: unknown bundle "${name}"`);
    await Assets.loadBundle(name, onProgress);
    this.loaded.add(name);
  }

  /** Registers a bundle that is not in the built manifest (an operator's remote bundle); an asset's `data.trim` frames it as a built one's does. */
  /**
   * One asset of a bundle, loaded on its own (`getLoadedAssetOrNull` reads it after): the boot bundle's logo, without
   * decoding the stills beside it (decision 69). Throws when the manifest does not list it or it fails.
   */
  public async loadAsset(bundle: string, path: string): Promise<void> {
    const alias = `${bundle}:${path}`;
    if (Assets.cache.has(alias)) return;
    if (!this.files[`${bundle}/${path}`]) throw new Error(`AssetRegistry: "${path}" is not in bundle "${bundle}" (alias ${alias})`);
    await Assets.load(alias);
  }

  public addBundle(name: string, assets: AssetManifestAsset[], files: Record<string, string> = {}): void {
    Assets.addBundle(name, assets);
    this.known.add(name);
    for (const asset of assets) {
      if (asset.data?.trim) this.trims.set(asset.alias, asset.data.trim);
    }
    Object.assign(this.files, files);
  }

  /** Returns the loaded asset for a bundle-relative path. Throws when the bundle/asset is not loaded. */
  public getLoadedAssetOrThrow<T>(bundle: string, path: string): T {
    const alias = `${bundle}:${path}`;
    const asset = Assets.get<T>(alias);
    if (asset === undefined || asset === null) {
      throw new Error(`AssetRegistry: asset "${path}" is not loaded from bundle "${bundle}" (alias ${alias})`);
    }
    return this.frameToTrim(alias, asset);
  }

  public getLoadedAssetOrNull<T>(bundle: string, path: string): T | null {
    const alias = `${bundle}:${path}`;
    if (!Assets.cache.has(alias)) return null; // `Assets.get` warns on a miss; a null probe is expected
    const asset = Assets.get<T>(alias);
    return asset === undefined ? null : this.frameToTrim(alias, asset);
  }

  /** URL of a bundle file by its extensionless path (audio, HTML, an image the DOM paints), or null when the manifest does not list it. */
  public resolveUrl(bundle: string, path: string): string | null {
    const rel = this.files[`${bundle}/${path}`];
    return rel ? `${this.baseUrl}/${rel}` : null;
  }

  /** `AudioBundleUrlResolver` for `createAudioService`. Remote refs resolve to `<url>/<key>.m4a`. */
  public readonly resolveAudioUrl = async (bundle: BundleRef, key: string): Promise<string | null> => {
    if (bundle.kind === "local") return this.resolveUrl(bundle.name, key);
    return `${bundle.url.replace(/\/$/, "")}/${key}.m4a`;
  };

  /**
   * A `JsonAssetSource` scoped to one bundle, loading JSON lazily through Pixi Assets. A file the manifest does not
   * list rejects: Pixi resolves an unknown alias as `undefined` (with a warning), and the locale loaders would read
   * keys off it — staging's "en-US", whose files do not exist, killed the boot that way (2026-10-04).
   */
  public bundleJson(bundle: string): JsonAssetSource {
    return {
      loadJson: async (path: string): Promise<unknown> => {
        const alias = `${bundle}:${path}`;
        if (Assets.cache.has(alias)) return Assets.get<unknown>(alias);
        if (!this.files[`${bundle}/${path}`]) throw new Error(`AssetRegistry: "${path}" is not in bundle "${bundle}" (alias ${alias})`);
        const loaded = await Assets.load<unknown>(alias);
        if (loaded === undefined) throw new Error(`AssetRegistry: "${path}" of bundle "${bundle}" loaded as nothing (alias ${alias})`);
        return loaded;
      },
    };
  }

  /**
   * The image as the game draws it: its opaque region, when the build measured transparent margins
   * around it. The served file keeps the margins — the build copies art byte for byte and only records
   * the rectangle (review finding 12) — so the crop is a texture frame over the loaded source, which
   * the GPU samples in place: no pixels are copied. Sized like the cropped file the build used to
   * write, so every `sprite.width/height = contentSize` stretches the same region as before.
   */
  private frameToTrim<T>(alias: string, asset: T): T {
    const trim = this.trims.get(alias);
    if (!trim) return asset;
    let texture = this.framed.get(alias);
    if (!texture) {
      texture = new Texture({ source: (asset as Texture).source, frame: new Rectangle(trim.x, trim.y, trim.width, trim.height) });
      this.framed.set(alias, texture);
    }
    return texture as T;
  }
}
